/**
 * LEAGUE SETTINGS — D-EZ-103 (flow), carrying D-EZ-83…87 and D-EZ-98.
 *
 * A league is created once, with a name and a set of rules, and every season it
 * runs is raced under those rules unless the player changes them FOR THE NEXT
 * SEASON. Two families, kept apart because they live in two different places in
 * the engine:
 *
 *   race   — the realism house rules (`HouseRuleOptions`, src/domain/houseRules.ts):
 *            track personality, superspeedway pack racing, laps lost to pace,
 *            clean air on restarts, the era dial. Default ON / modern. They ride
 *            to every race through `SeasonOptions.race`.
 *   sheet  — Howard's own season sheet switches (`SeasonHouseRules`): a DNF scores
 *            nothing, stage points off / tracked / counted. Default OFF. They ride
 *            through `SeasonOptions.houseRules`.
 *
 * WHERE THEY ARE KEPT. A live season already carries them inside
 * `SeasonState.options` (saved with the season head), a settled season carries
 * them on `CareerSeason.houseRules` / `CareerSeason.raceRules`, and the league
 * head carries the settings the NEXT season will start with (`settings` on the
 * save head — see `lib/leagueSlot.ts`). Pure: nothing here touches storage.
 */

import { HOUSE_RULES, type HouseRuleOptions } from '../domain/houseRules.ts';
import type { SeasonHouseRules, SeasonOptions } from '../domain/seasonPlay.ts';

export type StageMode = NonNullable<SeasonHouseRules['stagePoints']>;

export interface LeagueSettings {
  /** Realism house rules — absent field = ON (era: modern). */
  race: HouseRuleOptions;
  /** Season-sheet switches — absent field = OFF. */
  sheet: SeasonHouseRules;
}

/** What a new league starts with: every realism rule on, both sheet switches off. */
export const DEFAULT_LEAGUE_SETTINGS: Readonly<LeagueSettings> = Object.freeze({
  race: Object.freeze({}),
  sheet: Object.freeze({}),
});

/** The race rules that can be switched (era is its own dial). */
export type SwitchRule = Exclude<keyof HouseRuleOptions, 'era'>;
export const SWITCH_RULES: readonly SwitchRule[] = ['trackPersonality', 'packRacing', 'lapsToPace', 'cleanAir'];

/** Is this realism rule on? Absent means on. */
export function ruleOn(race: HouseRuleOptions, key: SwitchRule): boolean {
  return race[key] !== false;
}

/** Turn one realism rule on or off, keeping the stored shape minimal (off = `false`, on = absent). */
export function withRule(race: HouseRuleOptions, key: SwitchRule, on: boolean): HouseRuleOptions {
  const next: HouseRuleOptions = { ...race };
  if (on) delete next[key];
  else next[key] = false;
  return next;
}

export function withEra(race: HouseRuleOptions, era: 'modern' | '1990s'): HouseRuleOptions {
  const next: HouseRuleOptions = { ...race };
  if (era === 'modern') delete next.era;
  else next.era = '1990s';
  return next;
}

/**
 * Read anything that claims to be settings back into a clean shape. Unknown
 * keys are dropped and every value is checked, so a hand-edited file or an
 * older head can never smuggle a wrong type into `runRace`.
 */
export function normaliseSettings(raw: unknown): LeagueSettings {
  const r = raw && typeof raw === 'object' ? (raw as { race?: unknown; sheet?: unknown }) : {};
  return { race: normaliseRace(r.race), sheet: normaliseSheet(r.sheet) };
}

export function normaliseRace(raw: unknown): HouseRuleOptions {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out: HouseRuleOptions = {};
  for (const key of SWITCH_RULES) if (src[key] === false) out[key] = false;
  if (src.era === '1990s') out.era = '1990s';
  return out;
}

export function normaliseSheet(raw: unknown): SeasonHouseRules {
  const src = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const out: SeasonHouseRules = {};
  if (src.dnfScoresZero === true) out.dnfScoresZero = true;
  if (src.stagePoints === 'tracked' || src.stagePoints === 'counted') out.stagePoints = src.stagePoints;
  return out;
}

/** The `SeasonOptions` a season under these settings is begun with. */
export function seasonOptionsFor(settings: LeagueSettings, fieldSize: number): SeasonOptions {
  const race = normaliseRace(settings.race);
  const sheet = normaliseSheet(settings.sheet);
  return {
    fieldSize,
    ...(Object.keys(race).length ? { race } : {}),
    ...(Object.keys(sheet).length ? { houseRules: sheet } : {}),
  };
}

/** The settings a season was begun with, read back off its options. */
export function settingsOfSeasonOptions(options: SeasonOptions | null | undefined): LeagueSettings {
  return { race: normaliseRace(options?.race), sheet: normaliseSheet(options?.houseRules) };
}

/** The settings a settled season was raced and scored under. */
export function settingsOfCareer(career: { houseRules?: SeasonHouseRules; raceRules?: HouseRuleOptions } | null | undefined): LeagueSettings {
  return { race: normaliseRace(career?.raceRules), sheet: normaliseSheet(career?.houseRules) };
}

export function sameSettings(a: LeagueSettings, b: LeagueSettings): boolean {
  return JSON.stringify(normaliseSettings(a)) === JSON.stringify(normaliseSettings(b));
}

// ---------------------------------------------------------------------------
// Words — one plain line each, for the creation form and the settings panel
// ---------------------------------------------------------------------------

export interface RuleLine {
  key: string;
  /** Short name, no "House rule:" prefix. */
  label: string;
  /** One plain sentence. */
  summary: string;
  on: boolean;
  /** What it reads as when shown read-only. */
  state: string;
}

const RACE_LABEL: Record<SwitchRule, string> = {
  trackPersonality: 'Track personality',
  packRacing: 'Superspeedway pack racing',
  lapsToPace: 'Laps lost to pace',
  cleanAir: 'Clean air on restarts',
};

export function raceRuleLines(race: HouseRuleOptions): RuleLine[] {
  const lines: RuleLine[] = SWITCH_RULES.map((key) => {
    const info = HOUSE_RULES.find((r) => r.key === key);
    const on = ruleOn(race, key);
    return { key, label: RACE_LABEL[key], summary: info?.summary ?? '', on, state: on ? 'On' : 'Off' };
  });
  const modern = race.era !== '1990s';
  lines.push({
    key: 'era',
    label: 'Era',
    summary: modern
      ? HOUSE_RULES.find((r) => r.key === 'era')?.summary ?? ''
      : 'Cars of the 1990s: a mechanical problem usually ends the day, as in the printed game.',
    on: modern,
    state: modern ? 'Modern' : '1990s',
  });
  return lines;
}

export const STAGE_WORDS: Record<StageMode, { label: string; summary: string }> = {
  off: { label: 'No stages', summary: 'Points for the finish only, as in the printed game.' },
  tracked: {
    label: 'Stages shown',
    summary: 'The top ten at each third of the race are credited stage points, shown but not added.',
  },
  counted: {
    label: 'Stages counted',
    summary: 'The top ten at each third of the race earn stage points that count toward the title.',
  },
};

export function sheetRuleLines(sheet: SeasonHouseRules): RuleLine[] {
  const stage = sheet.stagePoints ?? 'off';
  return [
    {
      key: 'dnfScoresZero',
      label: 'No points for a DNF',
      summary: 'A car that is not running at the finish scores no finishing points.',
      on: sheet.dnfScoresZero === true,
      state: sheet.dnfScoresZero ? 'On' : 'Off',
    },
    {
      key: 'stagePoints',
      label: 'Stage points',
      summary: STAGE_WORDS[stage].summary,
      on: stage !== 'off',
      state: STAGE_WORDS[stage].label,
    },
  ];
}

/** One short line for a strip or a card: "All realism rules on · modern era · no stages". */
export function settingsSummary(settings: LeagueSettings): string {
  const race = normaliseRace(settings.race);
  const off = SWITCH_RULES.filter((k) => race[k] === false).map((k) => RACE_LABEL[k].toLowerCase());
  const parts = [
    off.length === 0 ? 'All realism rules on' : off.length === SWITCH_RULES.length ? 'Realism rules off' : `Off: ${off.join(', ')}`,
    race.era === '1990s' ? '1990s era' : 'modern era',
    settings.sheet.dnfScoresZero ? 'no points for a DNF' : null,
    STAGE_WORDS[settings.sheet.stagePoints ?? 'off'].label.toLowerCase(),
  ].filter((p): p is string => Boolean(p));
  return parts.join(' · ');
}
